import { call, delay, put, select, takeEvery } from "redux-saga/effects";
import { AxiosError } from "axios";
import { notification } from "antd";

import * as types from "../Types/MobilyticsActionTypes";
import * as actions from "../Actions/MobilyticsActions";
import * as api from "../API/MobilyticsApi";
import { PlaceParameters } from "../Types/MobilyticsActionTypes";
import {
    getNearestSafegraphLocationsByBrand, getNearestSafegraphLocationsByCategory,
    getSafeGraphLocation,
    ISafegraphGetNearestLocationsRequest
} from "../API/MobilyticsApi";
import { IApiResult } from "../../../Models/IApiResult";
import { IGCPBigQueryJob } from "../../../Models/IGCPBigQueryJob";
import { ISubmitMobilyticsJobRequest } from "../../../Models/Requests/ISubmitMobilyticsJobRequest";
import { getGeocodeFromLatLng, getPlaceDetails, searchAutocompleteWithLatLng } from "./PlaceSearchSagas";
import { IMapMarker, MapMarker } from "../Models/IMapMarker";
import {
    firstScenarioSelector,
    isCreatingCustomLocationSelector,
    scenarioFormStatesSelector,
} from "../Selectors/MobilyticsSelectors";
import { defaultScenarioFormState, IMobilyticsScenarioFormState } from "../Pages/Apps/Mobilytics/SubmitMobilyticsLogic";
import { PolygonType } from "../Models/Maps/Enums/PolygonType";
import { customPolygonFromPoints} from "../Logic/PolygonLogic";
import {
    setNearbyLocations,
    setSimilarLocations,
    setIsSearchingForNewLocation,
    setSelectedSafegraphLocation
} from "../Actions/MobilyticsActions";
import { ISafegraphLocation } from "../../../Models/ISafegraphLocation";
import { brandsSelector } from "../Selectors/BrandSelectors";
import { getAddressAndZipcodeFromGooglePlaceResult } from "../Logic/MobilyticsLogic";
import { IBrand, IServiceResult } from "../../../Models/Models";
import { mapMarkersSelector } from "../Selectors/MapSelectors";
import { setMapMarkers, setShowLoadingOverlay } from "../Actions/Actions";
import { ICustomPolygon } from "../Models/Maps/ICustomPolygon";
import { toTitleCase } from "../Util/StringUtil";
import { getFirstGeocodeResult, SupportedGeocoderCountry } from "../Util/GeocoderUtil";
import { isValidLatForMobilytics, isValidLngForMobilytics } from "../Util/LatLngUtil";
import { IReportProgress } from "../Models/IReportProgress";
import { setReportProgress } from "../Actions/ReportActions";
import { getPendingReportProgress } from "../Components/SubmittedReport/SubmittedReportLogic";

export const MAXIMUM_SCENARIOS = 2;

export function* watchMobilyticsSagas() {
    yield takeEvery(
        types.SUBMIT_MOBILYTICS_REPORT_INITIATE,
        submitMobilyticsReportSaga
    );

    yield takeEvery(
        types.ON_LOCATION_SELECTED_FOR_ANALYSIS_ACTION,
        onLocationSelectedForAnalysisSaga
    );

    yield takeEvery(
        types.GET_NEARBY_LOCATIONS_ACTION,
        getNearbySafegraphLocations
    );

    yield takeEvery(
        types.GET_SIMILAR_LOCATIONS_ACTION,
        getSimilarSafegraphLocations
    );
}

export function* submitMobilyticsReportSaga(action: types.MobilyticsActionType) {
    try {
        yield put(actions.setMostRecentlySubmittedMobilyticsReport([]));
        yield put(actions.setIsLoading(true));
        const job: IApiResult<IGCPBigQueryJob[]> = yield call(() =>
            api.submitMobilyticsReport(action.data as ISubmitMobilyticsJobRequest)
        );
        if (!job.success) {
            yield put(actions.submitMobilyticsReportFailure(job.message));
        }
        yield put(actions.submitMobilyticsReportSuccess(job.data));
        const startProgress: IReportProgress = getPendingReportProgress();
        yield put(setReportProgress(startProgress));
        const { lat, lng, placeId, title: name } = yield select(firstScenarioSelector);
        yield put(actions.setLocationDetails({
            lat, lng, placeId, name
        }));
        yield put(actions.setShouldShowEndOfReportScreen(true));
    } catch (e) {
        const errMsg = (e as AxiosError).message;
        notification.error({
            message: "Error",
            description: errMsg,
        });
        yield put(actions.submitMobilyticsReportFailure(e.message));
    } finally {
        yield put(actions.setIsLoading(false));
        /** reset the form for subsequent runs */
        yield put(actions.setScenarioFormStates([]));
    }
}

export function* onLocationSelectedForAnalysisSaga(action: types.MobilyticsActionType) {
    try {
        yield put(setShowLoadingOverlay(true));

        // Get current scenario form states
        const scenarioFormStates = yield(select(scenarioFormStatesSelector));
        if (scenarioFormStates.length === MAXIMUM_SCENARIOS) {
            return;
        }

        // Get all available brands
        const brands: IBrand[] = yield(select(brandsSelector));

        const isCreatingCustomLocation: boolean = yield(select(isCreatingCustomLocationSelector));

        // TODO - sniff around here
        // Check if the user is creating a custom location. If so, ignore and return here
        if (isCreatingCustomLocation) {
            return;
        }

        yield put(setIsSearchingForNewLocation(false));
        yield put(actions.setIsLoading(true));

        const { sessionToken, safegraphLocation, hasPolygon } = action.data as PlaceParameters;
        let { placeId } = action.data as PlaceParameters;

        let points = null;
        let details: google.maps.places.PlaceResult = null;
        let singleSafegraphResponse: ISafegraphLocation = null;
        let lat = null;
        let lng = null;
        let startingPoly: ICustomPolygon = null;

        // Check if there's a placeId. If so, attempt to get a SG location
        if (placeId) {
            details = yield getPlaceDetails(placeId, sessionToken);
            const addressZipcode = getAddressAndZipcodeFromGooglePlaceResult(details);
            lat = details.geometry.location.lat();
            lng = details.geometry.location.lng();
            // Get SafeGraph data for the selected location
            const safegraphResponse: IServiceResult<ISafegraphLocation> = yield getSafeGraphLocation({
                latitude: lat,
                longitude: lng,
                brandName: details.name,
                address: addressZipcode.address,
                zipcode: addressZipcode.zipcode,
            });

            if (hasPolygon) {
                const marker = new MapMarker(
                    placeId,                            // id
                    details.geometry.location.lat(),    // lat
                    details.geometry.location.lng(),    // lng
                    '',                                 // icon name
                    true,                               // selected
                    false,                              // loading
                    42,                                 // width
                    42,                                 // height
                    '',                                 // content
                    false,                              // info window open
                    details.formatted_address,          // name
                    false,                              // default marker
                );

                const existingMarkers: IMapMarker[] = yield select(mapMarkersSelector);
                return yield put(setMapMarkers([...existingMarkers, marker]));
            } else {
                // Set the points from the SafeGraph response polygon to the
                // selected location
                if (safegraphResponse?.data?.polygon) {
                    singleSafegraphResponse = safegraphResponse.data;

                    points = singleSafegraphResponse.polygon;

                    // Check if there is no brand name in the SG response. If not,
                    // set the brand name equal to the corresponding brand from the Brand table
                    if (!singleSafegraphResponse.brandName) {
                        const foundBrand: IBrand = brands.find(brand => brand.brandId === singleSafegraphResponse.brandId);
                        if (foundBrand) {
                            singleSafegraphResponse.brandName = foundBrand.brandName;
                        }
                    }

                    // Set selected safegraph location
                    yield put(setSelectedSafegraphLocation(singleSafegraphResponse));

                    // Create new polygon from this SG location
                    startingPoly = customPolygonFromPoints(points, true);
                }
                // Check if there are no points from the SafeGraph response. If not,
                // redirect the user to the create custom location screen
                if (!safegraphResponse.data || !safegraphResponse.data.polygon) {
                    // Dispatch action to set the selected location's address for a custom location
                    yield put(actions.setCreateCustomLocationParameters({
                        address: details.formatted_address,
                        lat,
                        lng,
                        placeId
                    }));
                    // Dispatch action to redirect user to the create custom location screen
                    yield put(actions.setIsCreatingCustomLocation(true));

                    // Set is drawing polygon and return here
                    return yield put(actions.setIsDrawingPolygon(true));
                }
            }
        }

        // Check if this location has already been selected. If so, return here
        if (scenarioFormStates.filter(state => state.id === placeId).length > 0) {
            return;
        }

        if (!points && safegraphLocation) {
            points = safegraphLocation.polygon;
        }

        const getSafegraphObjectValue = (property: string) => {
            if (singleSafegraphResponse) {
                return singleSafegraphResponse[property];
            }

            if (safegraphLocation) {
                return safegraphLocation[property];
            }

            return null;
        };

        // Map points to instance of Google Maps Lat/Lng
        const gMapPoints: google.maps.LatLng[] = points.map(point => {
            return new google.maps.LatLng(point.y, point.x);
        });

        // Create new scenario form state for the selected location

        if(!startingPoly) {
            startingPoly
                = {
                    polygonType: PolygonType.Custom,
                    id: safegraphLocation || singleSafegraphResponse
                        ? getSafegraphObjectValue("brandId")
                        : details.place_id,
                    name: safegraphLocation || singleSafegraphResponse
                        ? getSafegraphObjectValue("brandName")
                        : details.name,
                    isSelected: false,
                    borderSize: 0,
                    borderColor: '#000',
                    points: gMapPoints
                };
        }

        const formattedAddr = safegraphLocation || singleSafegraphResponse
            ? formatSafegraphAddress(getSafegraphObjectValue("address"), getSafegraphObjectValue("city"), getSafegraphObjectValue("state"), getSafegraphObjectValue("zip"))
            : details.formatted_address;

        if (!isValidLatForMobilytics(lat)) {
            lat = getSafegraphObjectValue("latitude") || details.geometry.location.lat();
        }

        if (!isValidLngForMobilytics(lng)) {
            lng = getSafegraphObjectValue("longitude") || details.geometry.location.lng();
        }

        if (!placeId) {
            // Need to get google placeId here
            const places: google.maps.GeocoderResult[] = yield getGeocodeFromLatLng(lat, lng);
            const place = getFirstGeocodeResult(places, SupportedGeocoderCountry.US);
            if (place) {
                placeId = place.place_id;
            }
        }

        const scenarioFormState: IMobilyticsScenarioFormState = {
            ...defaultScenarioFormState,
            id: placeId || safegraphLocation.locationId,
            title: safegraphLocation || singleSafegraphResponse
                ? getSafegraphObjectValue("brandName")
                : details.name,
            address: formattedAddr,
            polygon: {
                ...startingPoly,
                id: safegraphLocation || singleSafegraphResponse
                    ? getSafegraphObjectValue("brandId")
                    : details.place_id,
                name: safegraphLocation || singleSafegraphResponse
                    ? getSafegraphObjectValue("brandName")
                    : details.name,
            },
            brandId: getSafegraphObjectValue("brandId"),
            lat,
            lng,
            locationId: getSafegraphObjectValue("locationId"),
            placeId
        };

        yield put(actions.setScenarioFormStates([...scenarioFormStates, scenarioFormState]));
        yield put(actions.setLocationDetails({
            lat,
            lng,
            placeId,
            name: scenarioFormState.title
        }));

        const existingMarkers: IMapMarker[] = Array.from(yield select(mapMarkersSelector));

        if(safegraphLocation) {
            existingMarkers.push(
                new MapMarker(
                    "",                            // id
                    safegraphLocation.latitude,    // lat
                    safegraphLocation.longitude,    // lng
                    '',                                 // icon name
                    true,                               // selected
                    false,                              // loading
                    42,                                 // width
                    42,                                 // height
                    '',                                 // content
                    false,                              // info window open
                    scenarioFormState.address,          // name
                    false,                              // default marker
                ));
        }

        existingMarkers[existingMarkers.length - 1].id = scenarioFormState.id;

        yield put(setMapMarkers(existingMarkers));

    } catch (e) {
        yield put(actions.submitMobilyticsReportFailure(e.message));
    } finally {
        yield put(actions.setIsLoading(false));
        yield put(setShowLoadingOverlay(false));
    }
}

export function* getNearbySafegraphLocations() {
    try {
        const scenarioFormStates = yield select(scenarioFormStatesSelector);
        const location = scenarioFormStates[0];

        yield put(actions.setIsLoading(true));
        const request: ISafegraphGetNearestLocationsRequest = {
            ids: [location.brandId],
            latitude: location.lat,
            longitude: location.lng,
            excludeBrand: null
        };

        let nearbySafegraphLocations = yield getNearestSafegraphLocationsByBrand(request);

        // Filter selected location from nearby SG locations
        nearbySafegraphLocations = nearbySafegraphLocations.filter(nearbyLocation => nearbyLocation.locationId !== scenarioFormStates[0].locationId);

        yield put(setNearbyLocations(nearbySafegraphLocations));
    } catch (e) {
        yield put(actions.submitMobilyticsReportFailure(e.message));
    }  finally {
        yield put(actions.setIsLoading(false));
    }
}

export function* getSimilarSafegraphLocations() {
    try {
        yield put(actions.setIsLoading(true));
        const scenarioFormStates = yield select(scenarioFormStatesSelector);
        const location = scenarioFormStates[0];
        const brands = yield select(brandsSelector);

        // It's not guaranteed that the selected SG location will have a `naicsCode` property. So we can
        // find the corresponding brand for the selected location and use the brand's `naicsCode`
        const brand = brands.find(brand => brand.brandId === location.brandId);
        const similarRequest: ISafegraphGetNearestLocationsRequest = {
            ids: [brand.naicsCode],
            latitude: location.lat,
            longitude: location.lng,
            excludeBrand: location.brandId
        };
        const similarSafegraphLocations = yield(getNearestSafegraphLocationsByCategory(similarRequest));

        yield put(setSimilarLocations(similarSafegraphLocations));
    } catch (e) {
        yield put(actions.submitMobilyticsReportFailure(e.message));
    }  finally {
        yield put(actions.setIsLoading(false));
    }
}

const formatSafegraphAddress = (address: string, city: string, state: string, zipcode: string) => {
    return `${toTitleCase(address)}, ${toTitleCase(city)}, ${state} ${zipcode}`;
};